import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE, APP_CONFIG } from "../../utils/api";
import { auth } from "../../api/auth";
import { getCurrentUser } from "../../utils/users";

type Step = "email" | "code" | "prefilled";

const RESEND_COUNTDOWN = 60;

interface Props {
  onSuccess?: () => void;
}

export default function OtpAuthScreen({ onSuccess }: Props) {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showBuyButton, setShowBuyButton] = useState(false);
  const [prefillEmail, setPrefillEmail] = useState("");
  const [countdown, setCountdown] = useState(RESEND_COUNTDOWN);
  const [canResend, setCanResend] = useState(false);

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const pe = params.get("email") || "";
  if (pe) {
    setPrefillEmail(pe);
    setEmail(pe);
    setStep("prefilled");
    return;
  }
  emailInputRef.current?.focus();
}, []);


  useEffect(() => {
    if (step === "code" || step === "prefilled") {
      codeInputRef.current?.focus();
      startCountdown();
    }
    return () => {
      stopPolling();
      stopCountdown();
    };
  }, [step]);

  function startCountdown() {
    setCountdown(RESEND_COUNTDOWN);
    setCanResend(false);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current!);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  function stopCountdown() {
    if (countdownRef.current) clearInterval(countdownRef.current);
  }

  function stopPolling() {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    pollIntervalRef.current = null;
  }

  async function handleAuthSuccess(csrf?: string | null) {
    auth.completeLogin(csrf);
    stopPolling();
    stopCountdown();

    try {
      const user = await getCurrentUser();
      if (!user?.timezone) {
        navigate("/set-timezone");
      } else {
        navigate("/");
      }
    } catch {
      navigate("/set-timezone");
    }

    onSuccess?.();
  }

  function buildSendCodeBody(overrideEmail?: string) {
    const targetEmail = overrideEmail ?? email;
    const payload: Record<string, string> = {
      email: targetEmail,
    };
    return JSON.stringify(payload);
  }

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setShowBuyButton(false);
    try {
      const res = await fetch(`${API_BASE}/auth/bff/send-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: buildSendCodeBody(),
      });
      const data = await res.json().catch(() => ({}));
      if (data.ok) {
        setStep("code");
      } else {
        setError("Не удалось отправить код. Попробуйте снова");
      }
    } catch {
      setError("Не удалось отправить код. Попробуйте снова");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setShowBuyButton(false);
    try {
      const res = await fetch(`${API_BASE}/auth/bff/verify-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, code }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.ok) {
        await handleAuthSuccess(data.csrf);
      } else if (data.error === "expired_code") {
        setError("Код истёк. Запросите новый");
        setCanResend(true);
        stopCountdown();
      } else if (data.error === "too_many_attempts") {
        setError("Слишком много попыток. Запросите новый код");
        setCanResend(true);
        stopCountdown();
      } else if (data.error === "no_access") {
        setError("У этого аккаунта нет доступа к курсу.");
        setShowBuyButton(true);
      } else {
        setError("Неверный код. Попробуйте ещё раз");
      }
    } catch {
      setError("Ошибка сервера. Попробуйте снова");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError(null);
    setShowBuyButton(false);
    setCode("");
    try {
      const res = await fetch(`${API_BASE}/auth/bff/send-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: buildSendCodeBody(),
      });
      await res.json().catch(() => ({}));
      startCountdown();
    } catch {
      setError("Не удалось отправить код. Попробуйте снова");
    }
  };

  return (
    <main className="min-h-screen w-full flex flex-col bg-white">
      <section className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="rounded-2xl border border-gray-200 shadow-[0_6px_24px_rgba(0,0,0,0.06)] p-6 md:p-8">
            {step === "prefilled" ? (
              <>
                <h2 className="text-2xl font-bold mb-2 text-center">Введите код</h2>
                <p className="text-gray-500 text-sm text-center mb-6">
                  Введите 6-значный код из письма об оплате — мы отправили его на{" "}
                  <span className="font-semibold">{prefillEmail}</span>.
                </p>
                <form onSubmit={handleVerifyCode} className="flex flex-col gap-4">
                  <input
                    ref={codeInputRef}
                    type="tel"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    required
                    className="border border-gray-300 rounded-lg px-4 py-3 text-base text-center tracking-[0.4em] font-mono focus:outline-none focus:ring-2 focus:ring-black"
                  />
                  {error && (
                    <div className="text-red-600 text-sm text-center">{error}</div>
                  )}
                  {showBuyButton && (
                    <button
                      type="button"
                      onClick={() => navigate("/")}
                      className="w-full border border-black text-black rounded-lg py-3 font-semibold transition"
                    >
                      Купить курс
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={loading || code.length < 6}
                    className="bg-black text-white rounded-lg py-3 font-bold disabled:opacity-60 transition"
                  >
                    {loading ? "Проверяем..." : "Войти"}
                  </button>
                </form>
                <div className="mt-4 text-center text-sm text-gray-500">
                  {canResend ? (
                    <button onClick={handleResend} className="text-black underline underline-offset-2">
                      Отправить повторно
                    </button>
                  ) : (
                    <span>Отправить повторно через {countdown} сек</span>
                  )}
                </div>
                <div className="mt-3 text-center">
                  <button
                    onClick={() => {
                      stopPolling();
                      stopCountdown();
                      setStep("email");
                      setEmail("");
                      setCode("");
                      setError(null);
                      setShowBuyButton(false);
                      setTimeout(() => emailInputRef.current?.focus(), 50);
                    }}
                    className="text-xs text-gray-400 underline underline-offset-2"
                  >
                    Это не мой email
                  </button>
                </div>
              </>
            ) : step === "email" ? (

              <>
                <h2 className="text-2xl font-bold mb-2 text-center">Войти в {APP_CONFIG.brandName}</h2>
                <p className="text-gray-500 text-sm text-center mb-6">
                  Введите email — мы отправим код для входа
                </p>
                <form onSubmit={handleSendCode} className="flex flex-col gap-4">
                  <input
                    ref={emailInputRef}
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-black"
                  />
                  {error && (
                    <div className="text-red-600 text-sm text-center">{error}</div>
                  )}
                  {showBuyButton && (
                    <button
                      type="button"
                      onClick={() => navigate("/")}
                      className="w-full border border-black text-black rounded-lg py-3 font-semibold transition"
                    >
                      Купить курс
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={loading || !email}
                    className="bg-black text-white rounded-lg py-3 font-bold disabled:opacity-60 transition"
                  >
                    {loading ? "Отправляем..." : "Получить код"}
                  </button>
                </form>
              </>
            ) : (
              <>
                <h2 className="text-2xl font-bold mb-2 text-center">Введите код</h2>
                <p className="text-gray-500 text-sm text-center mb-6">
                  Если этот адрес зарегистрирован, письмо с кодом придёт в течение минуты.
                </p>
                <form onSubmit={handleVerifyCode} className="flex flex-col gap-4">
                  <input
                    ref={codeInputRef}
                    type="tel"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    required
                    className="border border-gray-300 rounded-lg px-4 py-3 text-base text-center tracking-[0.4em] font-mono focus:outline-none focus:ring-2 focus:ring-black"
                  />
                  {error && (
                    <div className="text-red-600 text-sm text-center">{error}</div>
                  )}
                  {showBuyButton && (
                    <button
                      type="button"
                      onClick={() => navigate("/")}
                      className="w-full border border-black text-black rounded-lg py-3 font-semibold transition"
                    >
                      Купить курс
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={loading || code.length < 6}
                    className="bg-black text-white rounded-lg py-3 font-bold disabled:opacity-60 transition"
                  >
                    {loading ? "Проверяем..." : "Войти"}
                  </button>
                </form>

                <div className="mt-4 text-center text-sm text-gray-500">
                  {canResend ? (
                    <button
                      onClick={handleResend}
                      className="text-black underline underline-offset-2"
                    >
                      Отправить повторно
                    </button>
                  ) : (
                    <span>Отправить повторно через {countdown} сек</span>
                  )}
                </div>

                <div className="mt-3 text-center">
                  <button
                    onClick={() => {
                      stopPolling();
                      stopCountdown();
                      setStep("email");
                      setCode("");
                      setError(null);
                      setShowBuyButton(false);
                    }}
                    className="text-xs text-gray-400 underline underline-offset-2"
                  >
                    Изменить email
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}